import React, { useEffect, useState, useMemo } from 'react'
import { Link, useLocation, useNavigate } from '@tanstack/react-router'
import {
  Briefcase,
  Users,
  Clock,
  DollarSign,
  FileText,
  AlertTriangle,
  CheckCircle,
  Download,
  Plus,
  ShieldCheck,
  Building,
  TrendingUp,
  Upload,
  Calendar,
  Zap,
  Receipt,
  Search,
  CheckSquare,
  Square,
  ArrowRight,
  Filter,
  Layers,
  FileSpreadsheet,
} from 'lucide-react'
import { AlphalistCompiler } from '@repo/entity-ledgersync'
import { LedgerSyncService } from '@repo/entity-ledgersync'
import type { BillingInvoice, ClientCompany, PettyCashVoucher, TimesheetEntry } from '@repo/entity-ledgersync'
import { ledgersyncSourceSummary } from '../../lib/ledgersync-source-summary'
import importPreview from '../../lib/ledgersync-import-preview.json'
import { ledgerSyncClientRepo, metadataResolver } from '../../lib/db'
import { useAuth } from '../../context/AuthContext'

const peso = (amount: number) => `₱${amount.toLocaleString('en-PH', { maximumFractionDigits: 2 })}`

export function LedgerSyncDashboard() {
  const location = useLocation()
  const navigate = useNavigate()
  const [activeRole, setActiveRole] = useState<'owner' | 'partner' | 'employee' | 'client'>('owner')
  const { user } = useAuth()

  // Domain policy comes from tenant metadata, with domain defaults during loading.
  const [ledgerSyncMetadata, setLedgerSyncMetadata] = useState(() => LedgerSyncService.getMetadata())

  useEffect(() => {
    let active = true
    void metadataResolver.getLedgerSyncConfig(user?.tenantId ?? 'default').then((config) => {
      if (active) setLedgerSyncMetadata(LedgerSyncService.getMetadata(config))
    })
    return () => { active = false }
  }, [user?.tenantId])

  // Determine active workflow from route pathname
  const currentPath = (location?.pathname || (typeof window !== 'undefined' ? window.location.pathname : '')).replace(/\/$/, '')
  const activeWorkflow = useMemo<'dashboard' | 'vaults' | 'alphalist' | 'petty-cash' | 'timesheets' | 'billing'>(() => {
    if (currentPath.includes('/vaults')) return 'vaults'
    if (currentPath.includes('/alphalist')) return 'alphalist'
    if (currentPath.includes('/petty-cash')) return 'petty-cash'
    if (currentPath.includes('/timesheets')) return 'timesheets'
    if (currentPath.includes('/billing')) return 'billing'
    return 'dashboard'
  }, [currentPath])

  // Client companies are loaded through the tenant-aware repository. The
  // repository middleware is the access boundary; the filter is a defensive
  // UI guard if a backing adapter ever returns a mixed tenant page.
  const tenantId = user?.tenantId ?? 'default'
  const [clients, setClients] = useState<ClientCompany[]>([])
  const [clientsLoading, setClientsLoading] = useState(true)
  const [clientLoadError, setClientLoadError] = useState<string | null>(null)

  useEffect(() => {
    let active = true
    setClientsLoading(true)
    setClientLoadError(null)

    void ledgerSyncClientRepo.findMany({
      page: 1,
      pageSize: 500,
      sort: [{ field: 'companyName', direction: 'asc' }],
    }).then((result) => {
      if (!active) return
      const items = 'items' in result ? result.items : []
      setClients(items.filter((client) => client.tenantId === tenantId))
    }).catch(() => {
      if (active) setClientLoadError('Client portfolios could not be loaded. Please retry.')
    }).finally(() => {
      if (active) setClientsLoading(false)
    })

    return () => { active = false }
  }, [tenantId])
  const [clientSearch, setClientSearch] = useState('')
  const [showAddClientModal, setShowAddClientModal] = useState(false)
  const [newClientName, setNewClientName] = useState('')
  const [newClientTin, setNewClientTin] = useState('')
  const [newClientRdo, setNewClientRdo] = useState('047')
  const [newClientRetainer, setNewClientRetainer] = useState('30000')

  const handleAddClient = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newClientName) return
    const now = Date.now()
    const newClient: ClientCompany = {
      id: `ledgersync-client-${now}`,
      tenantId,
      companyName: newClientName,
      tinNumber: newClientTin || '000-000-000-000',
      birBranchCode: '0000',
      rdoCode: newClientRdo || '047',
      industry: 'Unclassified',
      contactPerson: user?.fullName ?? 'Unassigned',
      contactEmail: user?.email ?? 'unassigned@example.invalid',
      contactPhone: 'Pending',
      assignedAccountantId: user?.id ?? 'unassigned',
      assignedPartnerId: user?.id ?? 'unassigned',
      monthlyRetainerAmount: parseFloat(newClientRetainer) || 0,
      status: 'onboarding',
      createdAt: now,
      updatedAt: now,
      deletedAt: null,
      version: 1,
      createdBy: user?.id ?? 'anonymous',
      updatedBy: user?.id ?? 'anonymous',
    }
    try {
      const persistedClient = await ledgerSyncClientRepo.create(newClient)
      setClients((current) => [persistedClient, ...current])
      setNewClientName('')
      setNewClientTin('')
      setShowAddClientModal(false)
    } catch {
      setClientLoadError('Client portfolio could not be saved. Please retry.')
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Petty Cash Vouchers State
  // ─────────────────────────────────────────────────────────────
  const [vouchers, setVouchers] = useState<PettyCashVoucher[]>([
    {
      id: 'pc-1',
      voucherNumber: 'PC-2026-001',
      requestedById: 'emp-101',
      requestedByName: 'Juan Dela Cruz',
      amount: 4500,
      purpose: 'BIR RDO 047 Document Filing Fees & Courier Services',
      category: 'bir_filing_fee',
      status: 'disbursed',
      requestedAt: Date.now() - 86400000 * 2,
    },
    {
      id: 'pc-2',
      voucherNumber: 'PC-2026-002',
      requestedById: 'emp-102',
      requestedByName: 'Maria Santos',
      amount: 42000,
      purpose: 'Client Onsite Audit Transportation Allowance',
      category: 'transportation',
      status: 'disbursed',
      requestedAt: Date.now() - 86400000,
    },
  ])
  const [showPettyCashModal, setShowPettyCashModal] = useState(false)
  const [newAmount, setNewAmount] = useState('1500')
  const [newPurpose, setNewPurpose] = useState('')
  const [newCategory, setNewCategory] = useState<'transportation' | 'office_supplies' | 'client_meeting' | 'bir_filing_fee' | 'other'>('bir_filing_fee')

  const pettyCashStatus = LedgerSyncService.evaluatePettyCashFund(
    vouchers,
    ledgerSyncMetadata.pettyCash.initialFloat,
    ledgerSyncMetadata.pettyCash.replenishmentThreshold,
  )

  const handleAddPettyCash = (e: React.FormEvent) => {
    e.preventDefault()
    if (!newPurpose) return
    const newVoucher: PettyCashVoucher = {
      id: `pc-${Date.now()}`,
      voucherNumber: `PC-2026-${(vouchers.length + 1).toString().padStart(3, '0')}`,
      requestedById: 'emp-curr',
      requestedByName: user?.fullName ?? 'Current User',
      amount: parseFloat(newAmount) || 0,
      purpose: newPurpose,
      category: newCategory,
      status: 'disbursed',
      requestedAt: Date.now(),
    }
    setVouchers([newVoucher, ...vouchers])
    setNewPurpose('')
    setShowPettyCashModal(false)
  }

  // ─────────────────────────────────────────────────────────────
  // BIR Alphalist Generator State
  // ─────────────────────────────────────────────────────────────
  const [selectedForm, setSelectedForm] = useState<'1604-C' | '1604-F' | '1604-E'>('1604-C')
  const [agentTin, setAgentTin] = useState('123456789')
  const [agentBranch, setAgentBranch] = useState('0000')
  const [agentRdo, setAgentRdo] = useState('047')
  const [agentName, setAgentName] = useState('SYCIP & ASSOCIATES ACCOUNTING FIRM')
  const [generatedDatContent, setGeneratedDatContent] = useState<string | null>(null)
  const [payees, setPayees] = useState([
    {
      tin: '111222333000',
      branchCode: '0000',
      name: 'DELA CRUZ, JUAN PEDRO',
      atcCode: 'WI100',
      grossPayment: 450000,
      taxRatePercent: 15,
      taxWithheld: 67500,
    },
    {
      tin: '444555666000',
      branchCode: '0000',
      name: 'SANTOS, MARIA CLARA',
      atcCode: 'WI100',
      grossPayment: 520000,
      taxRatePercent: 20,
      taxWithheld: 104000,
    },
    {
      tin: '777888999000',
      branchCode: '0000',
      name: 'REYES, JOSE MIGUEL',
      atcCode: 'WI100',
      grossPayment: 380000,
      taxRatePercent: 10,
      taxWithheld: 38000,
    },
  ])

  const handleGenerateAlphalist = () => {
    const dat = AlphalistCompiler.compileDatFile(
      {
        formType: selectedForm,
        withholdingAgentTin: agentTin,
        branchCode: agentBranch,
        rdoCode: agentRdo,
        taxableYear: 2025,
        agentName: agentName,
      },
      payees
    )
    setGeneratedDatContent(dat)
  }

  const handleDownloadDat = () => {
    if (!generatedDatContent) return
    const blob = new Blob([generatedDatContent], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${selectedForm.replace('-', '')}_${agentTin}_${agentBranch}_122025.dat`
    link.click()
  }

  // ─────────────────────────────────────────────────────────────
  // Timesheets & Billable Hours State
  // ─────────────────────────────────────────────────────────────
  const [timesheets, setTimesheets] = useState<TimesheetEntry[]>([
    {
      id: 'ts-1',
      accountantId: 'acc-1',
      accountantName: 'Juan Dela Cruz',
      clientId: 'c-1',
      clientName: 'Manila Trading & Logistics Corp',
      taskDescription: 'Q4 VAT & Expanded Withholding Tax Reconciliation',
      hoursSpent: 8.5,
      hourlyRate: 1500,
      billableAmount: 12750,
      isBilled: false,
      date: '2026-02-10',
    },
    {
      id: 'ts-2',
      accountantId: 'acc-2',
      accountantName: 'Maria Santos',
      clientId: 'c-2',
      clientName: 'BGC Tech Ventures Inc',
      taskDescription: 'BIR Audit Assessment Review & Supporting Schedules',
      hoursSpent: 12,
      hourlyRate: 1800,
      billableAmount: 21600,
      isBilled: false,
      date: '2026-02-12',
    },
    {
      id: 'ts-3',
      accountantId: 'acc-1',
      accountantName: 'Juan Dela Cruz',
      clientId: 'c-3',
      clientName: 'Cebu Holdings & Retail Co',
      taskDescription: 'Year-end Financial Statements & Inventory Audit',
      hoursSpent: 15,
      hourlyRate: 1500,
      billableAmount: 22500,
      isBilled: true,
      invoiceId: 'inv-101',
      date: '2026-02-08',
    },
  ])
  const [selectedTimesheetIds, setSelectedTimesheetIds] = useState<string[]>([])
  const [showTimesheetModal, setShowTimesheetModal] = useState(false)
  const [newTsClient, setNewTsClient] = useState('Manila Trading & Logistics Corp')
  const [newTsHours, setNewTsHours] = useState('4')
  const [newTsRate, setNewTsRate] = useState('1500')
  const [newTsTask, setNewTsTask] = useState('')

  const handleAddTimesheet = (e: React.FormEvent) => {
    e.preventDefault()
    if (!newTsTask) return
    const hours = parseFloat(newTsHours) || 1
    const rate = parseFloat(newTsRate) || 1500
    const newEntry: TimesheetEntry = {
      id: `ts-${Date.now()}`,
      accountantId: 'acc-curr',
      accountantName: user?.fullName ?? 'Juan Dela Cruz',
      clientId: `c-${Date.now()}`,
      clientName: newTsClient,
      taskDescription: newTsTask,
      hoursSpent: hours,
      hourlyRate: rate,
      billableAmount: hours * rate,
      isBilled: false,
      date: new Date().toISOString().slice(0, 10),
    }
    setTimesheets([newEntry, ...timesheets])
    setNewTsTask('')
    setShowTimesheetModal(false)
  }

  const toggleSelectTimesheet = (id: string) => {
    setSelectedTimesheetIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    )
  }

  const handleConvertTimesheetsToInvoice = () => {
    const selected = timesheets.filter(ts => selectedTimesheetIds.includes(ts.id) && !ts.isBilled)
    const first = selected[0]
    if (!first) return

    const targetClient = first.clientName
    const { totalBillable, items } = LedgerSyncService.convertTimesheetsToInvoice(targetClient, selected)

    const newInvoice: BillingInvoice = {
      id: `inv-${Date.now()}`,
      invoiceNumber: `INV-2026-${(invoices.length + 101).toString()}`,
      clientId: first.clientId,
      clientName: targetClient,
      period: 'February 2026',
      items,
      totalAmount: totalBillable,
      status: 'sent',
      dueDate: new Date(Date.now() + 86400000 * 15).toISOString().slice(0, 10),
      weeklyCollectionWeek: '2026-W07',
    }

    setInvoices([newInvoice, ...invoices])
    setTimesheets(timesheets.map(ts => selectedTimesheetIds.includes(ts.id) ? { ...ts, isBilled: true, invoiceId: newInvoice.id } : ts))
    setSelectedTimesheetIds([])
    navigate({ to: '/ledgersync/billing' })
  }

  // ─────────────────────────────────────────────────────────────
  // Billing Invoices State
  // ─────────────────────────────────────────────────────────────
  const [invoices, setInvoices] = useState<BillingInvoice[]>([
    {
      id: 'inv-101',
      invoiceNumber: 'INV-2026-101',
      clientId: 'c-3',
      clientName: 'Cebu Holdings & Retail Co',
      period: 'January 2026',
      items: [
        { description: 'Monthly Accounting Retainer Fee', amount: 28000 },
        { description: 'Year-end Financial Statements Audit', amount: 22500 },
      ],
      totalAmount: 50500,
      status: 'paid',
      dueDate: '2026-02-15',
      paidDate: '2026-02-14',
      weeklyCollectionWeek: '2026-W07',
    },
    {
      id: 'inv-102',
      invoiceNumber: 'INV-2026-102',
      clientId: 'c-1',
      clientName: 'Manila Trading & Logistics Corp',
      period: 'February 2026',
      items: [
        { description: 'Monthly Accounting Retainer Fee', amount: 35000 },
      ],
      totalAmount: 35000,
      status: 'sent',
      dueDate: '2026-02-28',
      weeklyCollectionWeek: '2026-W07',
    },
    {
      id: 'inv-103',
      invoiceNumber: 'INV-2026-103',
      clientId: 'c-2',
      clientName: 'BGC Tech Ventures Inc',
      period: 'February 2026',
      items: [
        { description: 'Monthly Retainer Fee', amount: 50000 },
      ],
      totalAmount: 50000,
      status: 'draft',
      dueDate: '2026-02-28',
      weeklyCollectionWeek: '2026-W07',
    },
  ])

  const handleMarkAsPaid = (invId: string) => {
    setInvoices(invoices.map(inv => inv.id === invId ? { ...inv, status: 'paid', paidDate: new Date().toISOString().slice(0, 10) } : inv))
  }

  // Sample Automation Triggers
  const automationRules = [
    {
      id: 'ar-1',
      name: 'Monthly Retainer Billing',
      trigger: '1st of Month at 8:00 AM',
      action: 'Generate and email monthly retainer invoices to all active clients',
      status: 'active',
    },
    {
      id: 'ar-2',
      name: 'BIR Tax Deadline Document Chaser',
      trigger: 'Tax Deadline - 5 Days',
      action: 'Scan client vaults and send automated reminder email for missing TINs/receipts',
      status: 'active',
    },
    {
      id: 'ar-3',
      name: 'Petty Cash Replenishment Alert',
      trigger: `Petty Cash Balance < ${peso(ledgerSyncMetadata.pettyCash.replenishmentThreshold)}`,
      action: 'Alert Managing Partner to disburse physical fund replenishment',
      status: 'active',
    },
    {
      id: 'ar-4',
      name: 'Weekly Collections Aggregator',
      trigger: 'Every Friday at 5:00 PM',
      action: 'Compile cash inflows into formatted PDF report and email to Owners/Partners',
      status: 'active',
    },
  ]

  // Filtered clients
  const filteredClients = clients.filter(c =>
    c.companyName.toLowerCase().includes(clientSearch.toLowerCase()) ||
    c.tinNumber.includes(clientSearch) ||
    c.assignedAccountantId.toLowerCase().includes(clientSearch.toLowerCase())
  )

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100 p-6 space-y-6">
      {/* Top Title Banner */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 bg-slate-800/80 backdrop-blur border border-slate-700 p-6 rounded-2xl shadow-xl">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-600/20 text-blue-400 rounded-xl border border-blue-500/30">
              <Briefcase className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white tracking-tight flex items-center gap-2">
                LedgerSync
                <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-blue-500/20 text-blue-300 border border-blue-400/30">
                  Philippines BIR Practice Management
                </span>
              </h1>
              <p className="text-sm text-slate-400">
                Multi-Tenant Practice Operating System • Alphalist .DAT Generator • Weekly Collections • Petty Cash
              </p>
            </div>
          </div>
        </div>

        {/* Role Selection Tabs */}
        <div className="flex items-center bg-slate-900/90 p-1.5 rounded-xl border border-slate-700/80">
          <button
            onClick={() => setActiveRole('owner')}
            className={`flex items-center gap-2 px-3.5 py-2 text-xs font-medium rounded-lg transition-all ${
              activeRole === 'owner'
                ? 'bg-blue-600 text-white shadow-md shadow-blue-900/40 font-semibold'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
            }`}
          >
            <TrendingUp className="w-3.5 h-3.5" />
            Owner (Partner)
          </button>
          <button
            onClick={() => setActiveRole('partner')}
            className={`flex items-center gap-2 px-3.5 py-2 text-xs font-medium rounded-lg transition-all ${
              activeRole === 'partner'
                ? 'bg-blue-600 text-white shadow-md shadow-blue-900/40 font-semibold'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
            }`}
          >
            <Users className="w-3.5 h-3.5" />
            Client Manager
          </button>
          <button
            onClick={() => setActiveRole('employee')}
            className={`flex items-center gap-2 px-3.5 py-2 text-xs font-medium rounded-lg transition-all ${
              activeRole === 'employee'
                ? 'bg-blue-600 text-white shadow-md shadow-blue-900/40 font-semibold'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
            }`}
          >
            <FileText className="w-3.5 h-3.5" />
            Accountant
          </button>
          <button
            onClick={() => setActiveRole('client')}
            className={`flex items-center gap-2 px-3.5 py-2 text-xs font-medium rounded-lg transition-all ${
              activeRole === 'client'
                ? 'bg-blue-600 text-white shadow-md shadow-blue-900/40 font-semibold'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
            }`}
          >
            <Building className="w-3.5 h-3.5" />
            Client Portal
          </button>
        </div>
      </div>

      {/* Navigation Sub-Tabs Bar */}
      <div className="flex items-center gap-2 border-b border-slate-700/80 pb-2 overflow-x-auto">
        <Link
          to="/ledgersync"
          className={`flex items-center gap-2 px-4 py-2 text-xs font-semibold rounded-lg transition-all ${
            activeWorkflow === 'dashboard'
              ? 'bg-blue-600 text-white shadow-sm'
              : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
          }`}
        >
          <Layers className="w-4 h-4" />
          Dashboard Overview
        </Link>
        <Link
          to="/ledgersync/vaults"
          className={`flex items-center gap-2 px-4 py-2 text-xs font-semibold rounded-lg transition-all ${
            activeWorkflow === 'vaults'
              ? 'bg-blue-600 text-white shadow-sm'
              : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
          }`}
        >
          <Building className="w-4 h-4" />
          Client Vaults
        </Link>
        <Link
          to="/ledgersync/alphalist"
          className={`flex items-center gap-2 px-4 py-2 text-xs font-semibold rounded-lg transition-all ${
            activeWorkflow === 'alphalist'
              ? 'bg-blue-600 text-white shadow-sm'
              : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
          }`}
        >
          <FileSpreadsheet className="w-4 h-4" />
          BIR Alphalist
        </Link>
        <Link
          to="/ledgersync/petty-cash"
          className={`flex items-center gap-2 px-4 py-2 text-xs font-semibold rounded-lg transition-all ${
            activeWorkflow === 'petty-cash'
              ? 'bg-blue-600 text-white shadow-sm'
              : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
          }`}
        >
          <DollarSign className="w-4 h-4" />
          Petty Cash
          {pettyCashStatus.isBelowThreshold && (
            <span className="w-2 h-2 rounded-full bg-amber-400 animate-ping" />
          )}
        </Link>
        <Link
          to="/ledgersync/timesheets"
          className={`flex items-center gap-2 px-4 py-2 text-xs font-semibold rounded-lg transition-all ${
            activeWorkflow === 'timesheets'
              ? 'bg-blue-600 text-white shadow-sm'
              : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
          }`}
        >
          <Clock className="w-4 h-4" />
          Timesheets
        </Link>
        <Link
          to="/ledgersync/billing"
          className={`flex items-center gap-2 px-4 py-2 text-xs font-semibold rounded-lg transition-all ${
            activeWorkflow === 'billing'
              ? 'bg-blue-600 text-white shadow-sm'
              : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
          }`}
        >
          <Receipt className="w-4 h-4" />
          Billing & Collections
        </Link>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          WORKFLOW VIEW 1: DASHBOARD OVERVIEW
          ───────────────────────────────────────────────────────────── */}
      {activeWorkflow === 'dashboard' && (
        <div className="space-y-6">
          {/* Petty Cash Alert if threshold hit */}
          {pettyCashStatus.isBelowThreshold && (
            <div className="bg-amber-950/80 border-2 border-amber-500/80 p-4 rounded-xl flex items-center justify-between gap-4 text-amber-200 shadow-lg">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-6 h-6 text-amber-400 shrink-0" />
                <div>
                  <h4 className="font-bold text-amber-300 text-sm">
                    AUTOMATION TRIGGER ALERT: Petty Cash Balance Below Threshold (&lt; {peso(ledgerSyncMetadata.pettyCash.replenishmentThreshold)})
                  </h4>
                  <p className="text-xs text-amber-200/90">
                    Current Petty Cash Balance is <span className="font-bold text-white">₱{pettyCashStatus.currentBalance.toLocaleString()}</span>. 
                    Managing Partner has been alerted to disburse float replenishment.
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowPettyCashModal(true)}
                className="px-3.5 py-1.5 text-xs font-semibold bg-amber-500 hover:bg-amber-400 text-slate-950 rounded-lg shrink-0 transition-colors"
              >
                Request Replenishment
              </button>
            </div>
          )}

          {/* Quick Status KPI Cards */}
          {activeRole !== 'client' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Link to="/ledgersync/billing" className="bg-slate-800/60 border border-slate-700/80 p-5 rounded-xl space-y-2 hover:border-blue-500/50 transition-colors">
                <div className="flex items-center justify-between text-slate-400">
                  <span className="text-xs font-medium">Friday Collection Goal</span>
                  <DollarSign className="w-4 h-4 text-emerald-400" />
                </div>
                <div className="text-2xl font-bold text-white">₱113,000</div>
                <div className="text-xs text-emerald-400 flex items-center gap-1 font-medium">
                  <TrendingUp className="w-3 h-3" />
                  88.5% weekly target collected
                </div>
              </Link>

              <Link to="/ledgersync/petty-cash" className="bg-slate-800/60 border border-slate-700/80 p-5 rounded-xl space-y-2 hover:border-amber-500/50 transition-colors">
                <div className="flex items-center justify-between text-slate-400">
                  <span className="text-xs font-medium">Petty Cash Fund</span>
                  <Clock className="w-4 h-4 text-amber-400" />
                </div>
                <div className={`text-2xl font-bold ${pettyCashStatus.isBelowThreshold ? 'text-amber-400' : 'text-white'}`}>
                  ₱{pettyCashStatus.currentBalance.toLocaleString()}
                </div>
                <div className="text-xs text-slate-400 font-medium">
                  Float limit: {peso(ledgerSyncMetadata.pettyCash.initialFloat)} • Threshold: &lt; {peso(ledgerSyncMetadata.pettyCash.replenishmentThreshold)}
                </div>
              </Link>

              <Link to="/ledgersync/vaults" className="bg-slate-800/60 border border-slate-700/80 p-5 rounded-xl space-y-2 hover:border-blue-500/50 transition-colors">
                <div className="flex items-center justify-between text-slate-400">
                  <span className="text-xs font-medium">Active Retainer Clients</span>
                  <Building className="w-4 h-4 text-blue-400" />
                </div>
                <div className="text-2xl font-bold text-white">{clientsLoading ? 'Loading…' : `${clients.length} Companies`}</div>
                <div className="text-xs text-blue-400 font-medium">
                  ₱{clients.reduce((sum, client) => sum + client.monthlyRetainerAmount, 0).toLocaleString()} monthly retainers
                </div>
              </Link>

              <Link to="/ledgersync/alphalist" className="bg-slate-800/60 border border-slate-700/80 p-5 rounded-xl space-y-2 hover:border-indigo-500/50 transition-colors">
                <div className="flex items-center justify-between text-slate-400">
                  <span className="text-xs font-medium">BIR Alphalist Status</span>
                  <ShieldCheck className="w-4 h-4 text-indigo-400" />
                </div>
                <div className="text-2xl font-bold text-white">Forms 1604-C/F/E</div>
                <div className="text-xs text-emerald-400 font-medium flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" />
                  100% BIR Validation Ready
                </div>
              </Link>
            </div>
          )}

          {/* Operating Snapshot */}
          <section className="bg-slate-800/60 border border-slate-700/80 p-6 rounded-2xl space-y-5">
            <div className="flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-xs uppercase tracking-wider text-blue-300 font-semibold">Source reconciliation</p>
                <h2 className="text-lg font-bold text-white">Tude & Villar operating snapshot</h2>
                <p className="text-xs text-slate-400">Evidence from the supplied workbooks through {ledgersyncSourceSummary.asOf}; not yet posted as ledger entries.</p>
              </div>
              <span className="text-xs rounded-full border border-amber-500/40 bg-amber-500/10 px-3 py-1 text-amber-200">Review required before import</span>
            </div>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              {[
                ['Clients', importPreview.counts.clients.toString()],
                ['Collections', peso(ledgersyncSourceSummary.collectionsTotal)],
                ['Open A/R', peso(ledgersyncSourceSummary.outstandingReceivables)],
                ['Cash advances', peso(ledgersyncSourceSummary.outstandingCashAdvances)],
              ].map(([label, value]) => (
                <div key={label} className="rounded-xl border border-slate-700 bg-slate-900/70 p-4">
                  <p className="text-xs text-slate-400">{label}</p>
                  <p className="mt-1 text-lg font-bold text-white">{value}</p>
                </div>
              ))}
            </div>
            <div className="grid gap-3 lg:grid-cols-2">
              <div className="rounded-xl border border-slate-700 bg-slate-900/50 p-4">
                <h3 className="text-sm font-semibold text-slate-200">Collections mix</h3>
                <div className="mt-3 space-y-2 text-xs">
                  {Object.entries(ledgersyncSourceSummary.collectionsByCategory).map(([category, amount]) => (
                    <div key={category} className="flex items-center justify-between gap-3 text-slate-300">
                      <span className="capitalize">{category.replace(/([A-Z])/g, ' $1')}</span>
                      <span className="font-mono text-emerald-300">{peso(amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4">
                <h3 className="text-sm font-semibold text-amber-200">Import exceptions ({importPreview.counts.exceptions})</h3>
                <ul className="mt-3 space-y-2 text-xs text-amber-100/80">
                  {ledgersyncSourceSummary.dataQualityFlags.map((flag) => <li key={flag}>• {flag}</li>)}
                </ul>
              </div>
            </div>
          </section>

          {/* Quick Workspaces Navigation Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link
              to="/ledgersync/vaults"
              className="bg-slate-800/70 border border-slate-700 p-5 rounded-xl hover:bg-slate-800 transition-all group flex flex-col justify-between"
            >
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Building className="w-6 h-6 text-blue-400" />
                  <span className="text-xs text-slate-400">{clientsLoading ? 'Loading…' : `${clients.length} Vaults`}</span>
                </div>
                <h3 className="font-bold text-white text-base">Client Company Vaults</h3>
                <p className="text-xs text-slate-400">Multi-tenant client records, TIN compliance, and cloud document vaults.</p>
              </div>
              <div className="mt-4 flex items-center gap-1 text-xs font-semibold text-blue-400 group-hover:translate-x-1 transition-transform">
                Open Client Vaults <ArrowRight className="w-3.5 h-3.5" />
              </div>
            </Link>

            <Link
              to="/ledgersync/alphalist"
              className="bg-slate-800/70 border border-slate-700 p-5 rounded-xl hover:bg-slate-800 transition-all group flex flex-col justify-between"
            >
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <FileSpreadsheet className="w-6 h-6 text-rose-400" />
                  <span className="text-xs text-emerald-400 font-medium">BIR Ready</span>
                </div>
                <h3 className="font-bold text-white text-base">BIR Alphalist .DAT Compiler</h3>
                <p className="text-xs text-slate-400">Compile BIR 1604-C, 1604-F, and 1604-E schedules into official format.</p>
              </div>
              <div className="mt-4 flex items-center gap-1 text-xs font-semibold text-rose-400 group-hover:translate-x-1 transition-transform">
                Compile Alphalist <ArrowRight className="w-3.5 h-3.5" />
              </div>
            </Link>

            <Link
              to="/ledgersync/timesheets"
              className="bg-slate-800/70 border border-slate-700 p-5 rounded-xl hover:bg-slate-800 transition-all group flex flex-col justify-between"
            >
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Clock className="w-6 h-6 text-amber-400" />
                  <span className="text-xs text-slate-400">{timesheets.filter(t => !t.isBilled).length} Unbilled</span>
                </div>
                <h3 className="font-bold text-white text-base">Timesheets & Billable Hours</h3>
                <p className="text-xs text-slate-400">Track hourly audit tasks and convert timesheets into client invoices.</p>
              </div>
              <div className="mt-4 flex items-center gap-1 text-xs font-semibold text-amber-400 group-hover:translate-x-1 transition-transform">
                Track Hours <ArrowRight className="w-3.5 h-3.5" />
              </div>
            </Link>
          </div>

          {/* Automation Rules (Owner only) */}
          {activeRole === 'owner' && (
            <div className="bg-slate-800/60 border border-slate-700/80 p-6 rounded-2xl space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-white text-base flex items-center gap-2">
                  <Zap className="w-5 h-5 text-amber-400" />
                  Practice Automation Engine
                </h3>
                <span className="text-xs text-amber-400 font-semibold px-2 py-0.5 rounded bg-amber-500/10 border border-amber-500/30">
                  IF/THEN Logic Active
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {automationRules.map((r) => (
                  <div key={r.id} className="bg-slate-900/80 border border-slate-700/60 p-3.5 rounded-xl space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-sm text-slate-100">{r.name}</span>
                      <span className="text-[11px] font-mono px-2 py-0.5 rounded bg-blue-900/40 text-blue-300 border border-blue-700/50">
                        {r.trigger}
                      </span>
                    </div>
                    <p className="text-xs text-slate-400">{r.action}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          WORKFLOW VIEW 2: CLIENT VAULTS
          ───────────────────────────────────────────────────────────── */}
      {activeWorkflow === 'vaults' && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-slate-800/60 border border-slate-700 p-6 rounded-2xl">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-blue-600/20 text-blue-400 rounded-lg">
                <Building className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Client Company Portfolios & Vaults</h2>
                <p className="text-xs text-slate-400 mt-0.5">
                  Multi-tenant isolated client archives, Philippine BIR TIN validations, and tax return filing vaults.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowAddClientModal(true)}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white font-medium text-xs rounded-lg transition-colors flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Client Company
              </button>
            </div>
          </div>

          {/* Search bar */}
          <div className="flex items-center gap-3 bg-slate-800/60 border border-slate-700 p-3 rounded-xl">
            <Search className="w-4 h-4 text-slate-400 ml-2" />
            <input
              type="text"
              placeholder="Search by company name, TIN, or assigned accountant..."
              value={clientSearch}
              onChange={(e) => setClientSearch(e.target.value)}
              className="bg-transparent text-sm text-slate-200 outline-none w-full"
            />
          </div>

          {/* Client Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {clientsLoading ? (
              <div className="col-span-full rounded-xl border border-slate-700 bg-slate-800/60 p-8 text-center text-sm text-slate-400">
                Loading tenant client portfolios…
              </div>
            ) : clientLoadError ? (
              <div role="alert" className="col-span-full rounded-xl border border-rose-500/40 bg-rose-500/10 p-8 text-center text-sm text-rose-200">
                {clientLoadError}
              </div>
            ) : filteredClients.length === 0 ? (
              <div className="col-span-full rounded-xl border border-slate-700 bg-slate-800/60 p-8 text-center text-sm text-slate-400">
                {clientSearch ? 'No tenant client portfolios match this search.' : 'No client portfolios have been created for this tenant.'}
              </div>
            ) : (
              filteredClients.map((c) => (
              <div key={c.id} className="bg-slate-800/80 border border-slate-700 p-5 rounded-xl space-y-4 hover:border-slate-600 transition-all">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-bold text-white text-base">{c.companyName}</h3>
                    <p className="text-xs text-slate-400 font-mono mt-0.5">TIN: {c.tinNumber} • RDO {c.rdoCode}</p>
                  </div>
                  {LedgerSyncService.validateClientForPosting(c, ledgerSyncMetadata).length > 0 ? (
                    <span className="px-2 py-0.5 text-[10px] font-semibold bg-amber-500/20 text-amber-400 rounded-full border border-amber-500/30">
                      TIN review required
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 text-[10px] font-semibold bg-emerald-500/20 text-emerald-400 rounded-full border border-emerald-500/30">
                      {c.status}
                    </span>
                  )}
                </div>

                <div className="bg-slate-900/60 p-3 rounded-lg border border-slate-700/50 space-y-1.5 text-xs text-slate-300">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Monthly Retainer:</span>
                    <span className="font-bold text-emerald-400">{peso(c.monthlyRetainerAmount)}/mo</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Assigned Partner:</span>
                    <span className="text-slate-200">{c.assignedPartnerId}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Accountant:</span>
                    <span className="text-slate-200">{c.assignedAccountantId}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Industry:</span>
                    <span className="text-blue-400 font-semibold">{c.industry}</span>
                  </div>
                </div>

                <div className="flex items-center gap-2 pt-2 border-t border-slate-700/50">
                  <button className="flex-1 py-1.5 text-xs bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg transition-colors flex items-center justify-center gap-1.5">
                    <Upload className="w-3.5 h-3.5" />
                    Upload File
                  </button>
                  <button className="flex-1 py-1.5 text-xs bg-blue-600/20 hover:bg-blue-600/30 text-blue-300 rounded-lg border border-blue-500/30 transition-colors flex items-center justify-center gap-1.5">
                    <FileText className="w-3.5 h-3.5" />
                    View Vault
                  </button>
                </div>
              </div>
            ))
          )}
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          WORKFLOW VIEW 3: BIR ALPHALIST COMPILER
          ───────────────────────────────────────────────────────────── */}
      {activeWorkflow === 'alphalist' && (
        <div className="space-y-6">
          <div className="bg-slate-800/60 border border-slate-700/80 p-6 rounded-2xl space-y-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-blue-600/20 text-blue-400 rounded-lg">
                  <FileSpreadsheet className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="font-bold text-white text-lg">BIR Alphalist .DAT File Compiler</h2>
                  <p className="text-xs text-slate-400">Automated generation of BIR-compliant .DAT files for forms 1604-C, 1604-F, and 1604-E</p>
                </div>
              </div>
              <span className="px-2.5 py-1 text-xs font-medium bg-emerald-500/20 text-emerald-400 rounded-full border border-emerald-500/30">
                BIR Validated Format
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-300 mb-1">Form Type</label>
                <select
                  value={selectedForm}
                  onChange={(e) => setSelectedForm(e.target.value as any)}
                  className="w-full bg-slate-900 border border-slate-700 text-slate-200 rounded-lg px-3 py-2 text-xs focus:ring-2 focus:ring-blue-500 outline-none"
                >
                  <option value="1604-C">Form 1604-C (Compensation Withholding)</option>
                  <option value="1604-F">Form 1604-F (Final Withholding)</option>
                  <option value="1604-E">Form 1604-E (Expanded Withholding)</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-300 mb-1">Withholding Agent TIN</label>
                <input
                  type="text"
                  value={agentTin}
                  onChange={(e) => setAgentTin(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-700 text-slate-200 rounded-lg px-3 py-2 text-xs focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-300 mb-1">Branch Code</label>
                <input
                  type="text"
                  value={agentBranch}
                  onChange={(e) => setAgentBranch(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-700 text-slate-200 rounded-lg px-3 py-2 text-xs focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-300 mb-1">RDO Code</label>
                <input
                  type="text"
                  value={agentRdo}
                  onChange={(e) => setAgentRdo(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-700 text-slate-200 rounded-lg px-3 py-2 text-xs focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>
            </div>

            {/* Payee Schedule List */}
            <div className="space-y-3 pt-2">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-200">Payee / Employee Schedule Entries</h3>
                <span className="text-xs text-slate-400">{payees.length} Payees Included</span>
              </div>
              <div className="overflow-x-auto rounded-xl border border-slate-700">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-900/90 text-slate-400 uppercase tracking-wider text-[10px]">
                    <tr>
                      <th className="p-3">TIN</th>
                      <th className="p-3">Branch</th>
                      <th className="p-3">Payee Name</th>
                      <th className="p-3">ATC</th>
                      <th className="p-3 text-right">Gross Payment</th>
                      <th className="p-3 text-right">Tax Rate</th>
                      <th className="p-3 text-right">Tax Withheld</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800 bg-slate-900/40">
                    {payees.map((p, idx) => (
                      <tr key={idx} className="hover:bg-slate-800/50">
                        <td className="p-3 font-mono text-slate-300">{p.tin}</td>
                        <td className="p-3 font-mono text-slate-400">{p.branchCode}</td>
                        <td className="p-3 font-medium text-white">{p.name}</td>
                        <td className="p-3 font-mono text-blue-400">{p.atcCode}</td>
                        <td className="p-3 text-right font-mono text-slate-200">₱{p.grossPayment.toLocaleString()}</td>
                        <td className="p-3 text-right font-mono text-slate-300">{p.taxRatePercent}%</td>
                        <td className="p-3 text-right font-mono text-emerald-400 font-bold">₱{p.taxWithheld.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={handleGenerateAlphalist}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white font-medium text-xs rounded-lg transition-colors flex items-center gap-2 shadow-lg shadow-blue-900/30"
              >
                <Zap className="w-3.5 h-3.5" />
                Compile BIR .DAT File
              </button>
              {generatedDatContent && (
                <button
                  onClick={handleDownloadDat}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-medium text-xs rounded-lg transition-colors flex items-center gap-2"
                >
                  <Download className="w-3.5 h-3.5" />
                  Download .DAT File
                </button>
              )}
            </div>

            {generatedDatContent && (
              <div className="space-y-2 pt-2">
                <div className="flex items-center justify-between text-xs text-slate-400">
                  <span>Compiled File Preview (Header, Details & Control Records):</span>
                  <span className="text-emerald-400 font-medium">Ready for BIR Submission</span>
                </div>
                <pre className="bg-slate-950 p-4 rounded-xl text-xs text-emerald-400 font-mono overflow-x-auto border border-slate-800 max-h-56 leading-relaxed">
                  {generatedDatContent}
                </pre>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          WORKFLOW VIEW 4: PETTY CASH
          ───────────────────────────────────────────────────────────── */}
      {activeWorkflow === 'petty-cash' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-slate-800/60 border border-slate-700 p-6 rounded-2xl space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-amber-600/20 text-amber-400 rounded-lg">
                    <DollarSign className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-white">Petty Cash Fund & Vouchers</h2>
                    <p className="text-xs text-slate-400 mt-0.5">
                      Daily office and filing fee disbursements with automated threshold tracking (&lt; {peso(ledgerSyncMetadata.pettyCash.replenishmentThreshold)}).
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowPettyCashModal(true)}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white font-medium text-xs rounded-lg transition-colors flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  New Voucher Request
                </button>
              </div>

              {/* Vouchers Table */}
              <div className="overflow-x-auto rounded-xl border border-slate-700">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-900/90 text-slate-400 uppercase tracking-wider text-[10px]">
                    <tr>
                      <th className="p-3">Voucher #</th>
                      <th className="p-3">Requested By</th>
                      <th className="p-3">Category</th>
                      <th className="p-3">Purpose</th>
                      <th className="p-3 text-right">Amount</th>
                      <th className="p-3 text-right">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800 bg-slate-900/40">
                    {vouchers.map((v) => (
                      <tr key={v.id} className="hover:bg-slate-800/50">
                        <td className="p-3 font-mono font-bold text-slate-300">{v.voucherNumber}</td>
                        <td className="p-3 text-white">{v.requestedByName}</td>
                        <td className="p-3">
                          <span className="capitalize px-2 py-0.5 rounded bg-slate-800 text-slate-300 text-[11px]">
                            {v.category.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="p-3 text-slate-300 max-w-xs truncate">{v.purpose}</td>
                        <td className="p-3 text-right font-mono font-bold text-amber-400">₱{v.amount.toLocaleString()}</td>
                        <td className="p-3 text-right">
                          <span className="capitalize px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500/20 text-emerald-400">
                            {v.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Fund Summary Sidebar */}
            <div className="bg-slate-800/60 border border-slate-700 p-6 rounded-2xl space-y-4">
              <h3 className="font-bold text-white text-base">Fund Status</h3>
              <div className="p-4 rounded-xl bg-slate-900/80 border border-slate-700/60 space-y-2">
                <div className="text-xs text-slate-400">Current Petty Cash Balance</div>
                <div className={`text-3xl font-bold ${pettyCashStatus.isBelowThreshold ? 'text-amber-400' : 'text-emerald-400'}`}>
                  ₱{pettyCashStatus.currentBalance.toLocaleString()}
                </div>
                <div className="text-xs text-slate-400">
                  Initial Float: {peso(ledgerSyncMetadata.pettyCash.initialFloat)}
                </div>
              </div>

              {pettyCashStatus.isBelowThreshold && (
                <div className="p-3 rounded-xl bg-amber-950/60 border border-amber-500/50 text-xs text-amber-200 space-y-2">
                  <div className="font-bold text-amber-300 flex items-center gap-1.5">
                    <AlertTriangle className="w-4 h-4" /> Threshold Breached
                  </div>
                  <p>Fund is below {peso(ledgerSyncMetadata.pettyCash.replenishmentThreshold)}. An automatic replenishment voucher is recommended.</p>
                </div>
              )}

              <div className="space-y-2 pt-2 text-xs">
                <div className="flex justify-between text-slate-400">
                  <span>Total Disbursed:</span>
                  <span className="font-bold text-slate-200">₱{vouchers.reduce((s, v) => s + v.amount, 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-slate-400">
                  <span>Active Vouchers:</span>
                  <span className="font-bold text-slate-200">{vouchers.length}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          WORKFLOW VIEW 5: TIMESHEETS & BILLABLE HOURS
          ───────────────────────────────────────────────────────────── */}
      {activeWorkflow === 'timesheets' && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-slate-800/60 border border-slate-700 p-6 rounded-2xl">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-amber-600/20 text-amber-400 rounded-lg">
                <Clock className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Timesheets & Billable Hours Tracking</h2>
                <p className="text-xs text-slate-400 mt-0.5">
                  Log audit, compliance, and tax preparation hours and convert billable tasks to client invoices.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowTimesheetModal(true)}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white font-medium text-xs rounded-lg transition-colors flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Log Hours
              </button>
              {selectedTimesheetIds.length > 0 && (
                <button
                  onClick={handleConvertTimesheetsToInvoice}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-medium text-xs rounded-lg transition-colors flex items-center gap-2"
                >
                  <Receipt className="w-4 h-4" />
                  Generate Invoice ({selectedTimesheetIds.length})
                </button>
              )}
            </div>
          </div>

          {/* Timesheets Table */}
          <div className="bg-slate-800/60 border border-slate-700 rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-900/90 text-slate-400 uppercase tracking-wider text-[10px]">
                  <tr>
                    <th className="p-3 w-10 text-center">Select</th>
                    <th className="p-3">Date</th>
                    <th className="p-3">Accountant</th>
                    <th className="p-3">Client Company</th>
                    <th className="p-3">Task Description</th>
                    <th className="p-3 text-right">Hours</th>
                    <th className="p-3 text-right">Rate/Hr</th>
                    <th className="p-3 text-right">Billable Amount</th>
                    <th className="p-3 text-center">Billed Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800 bg-slate-900/40">
                  {timesheets.map((ts) => (
                    <tr key={ts.id} className="hover:bg-slate-800/50">
                      <td className="p-3 text-center">
                        {!ts.isBilled ? (
                          <button
                            onClick={() => toggleSelectTimesheet(ts.id)}
                            className="text-slate-400 hover:text-blue-400"
                          >
                            {selectedTimesheetIds.includes(ts.id) ? (
                              <CheckSquare className="w-4 h-4 text-blue-400" />
                            ) : (
                              <Square className="w-4 h-4" />
                            )}
                          </button>
                        ) : (
                          <span className="text-slate-600 text-xs">✓</span>
                        )}
                      </td>
                      <td className="p-3 font-mono text-slate-400">{ts.date}</td>
                      <td className="p-3 text-white font-medium">{ts.accountantName}</td>
                      <td className="p-3 text-blue-300">{ts.clientName}</td>
                      <td className="p-3 text-slate-300">{ts.taskDescription}</td>
                      <td className="p-3 text-right font-mono text-slate-200">{ts.hoursSpent}h</td>
                      <td className="p-3 text-right font-mono text-slate-400">₱{ts.hourlyRate.toLocaleString()}</td>
                      <td className="p-3 text-right font-mono font-bold text-emerald-400">₱{ts.billableAmount.toLocaleString()}</td>
                      <td className="p-3 text-center">
                        {ts.isBilled ? (
                          <span className="px-2 py-0.5 text-[10px] font-semibold bg-emerald-500/20 text-emerald-400 rounded-full">
                            Billed ({ts.invoiceId})
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 text-[10px] font-semibold bg-amber-500/20 text-amber-400 rounded-full">
                            Unbilled
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          WORKFLOW VIEW 6: BILLING & WEEKLY COLLECTIONS
          ───────────────────────────────────────────────────────────── */}
      {activeWorkflow === 'billing' && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-slate-800/60 border border-slate-700 p-6 rounded-2xl">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-purple-600/20 text-purple-400 rounded-lg">
                <Receipt className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Billing Invoices & Weekly Collections</h2>
                <p className="text-xs text-slate-400 mt-0.5">
                  Client retainers, timesheet billings, Friday 5 PM collection aggregations, and receipt tracking.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs px-3 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 font-semibold">
                Weekly Collections: {peso(invoices.filter(i => i.status === 'paid').reduce((s, i) => s + i.totalAmount, 0))}
              </span>
            </div>
          </div>

          {/* Invoices List */}
          <div className="bg-slate-800/60 border border-slate-700 rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-900/90 text-slate-400 uppercase tracking-wider text-[10px]">
                  <tr>
                    <th className="p-3">Invoice #</th>
                    <th className="p-3">Client Company</th>
                    <th className="p-3">Billing Period</th>
                    <th className="p-3">Line Items</th>
                    <th className="p-3 text-right">Total Amount</th>
                    <th className="p-3">Due Date</th>
                    <th className="p-3 text-center">Status</th>
                    <th className="p-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800 bg-slate-900/40">
                  {invoices.map((inv) => (
                    <tr key={inv.id} className="hover:bg-slate-800/50">
                      <td className="p-3 font-mono font-bold text-white">{inv.invoiceNumber}</td>
                      <td className="p-3 text-blue-300 font-medium">{inv.clientName}</td>
                      <td className="p-3 text-slate-300">{inv.period}</td>
                      <td className="p-3 text-slate-400">{inv.items.length} items</td>
                      <td className="p-3 text-right font-mono font-bold text-emerald-400">₱{inv.totalAmount.toLocaleString()}</td>
                      <td className="p-3 font-mono text-slate-400">{inv.dueDate}</td>
                      <td className="p-3 text-center">
                        <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full capitalize ${
                          inv.status === 'paid'
                            ? 'bg-emerald-500/20 text-emerald-400'
                            : inv.status === 'sent'
                            ? 'bg-blue-500/20 text-blue-400'
                            : 'bg-slate-700 text-slate-300'
                        }`}>
                          {inv.status}
                        </span>
                      </td>
                      <td className="p-3 text-right">
                        {inv.status !== 'paid' && (
                          <button
                            onClick={() => handleMarkAsPaid(inv.id)}
                            className="px-2.5 py-1 bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-300 border border-emerald-500/30 rounded text-[11px] font-semibold transition-colors"
                          >
                            Mark Paid
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          MODALS
          ───────────────────────────────────────────────────────────── */}

      {/* New Petty Cash Modal */}
      {showPettyCashModal && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-slate-800 border border-slate-700 p-6 rounded-2xl w-full max-w-md space-y-4 shadow-2xl">
            <h3 className="text-lg font-bold text-white">Submit Petty Cash Request</h3>
            <form onSubmit={handleAddPettyCash} className="space-y-3 text-xs">
              <div>
                <label className="block text-slate-300 mb-1">Amount (PHP ₱)</label>
                <input
                  type="number"
                  value={newAmount}
                  onChange={(e) => setNewAmount(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-700 text-white rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-slate-300 mb-1">Category</label>
                <select
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value as any)}
                  className="w-full bg-slate-900 border border-slate-700 text-white rounded-lg p-2 text-xs outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="bir_filing_fee">BIR Filing Fee / Taxes</option>
                  <option value="transportation">Client Onsite Transportation</option>
                  <option value="office_supplies">Office Supplies & Printing</option>
                  <option value="client_meeting">Client Meeting Expense</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-slate-300 mb-1">Purpose & Description</label>
                <textarea
                  value={newPurpose}
                  onChange={(e) => setNewPurpose(e.target.value)}
                  rows={3}
                  className="w-full bg-slate-900 border border-slate-700 text-white rounded-lg p-2 text-xs outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Details of expense..."
                  required
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowPettyCashModal(false)}
                  className="px-4 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white font-medium rounded-lg transition-colors"
                >
                  Submit Request
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Client Modal */}
      {showAddClientModal && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-slate-800 border border-slate-700 p-6 rounded-2xl w-full max-w-md space-y-4 shadow-2xl">
            <h3 className="text-lg font-bold text-white">Add Client Company</h3>
            <form onSubmit={handleAddClient} className="space-y-3 text-xs">
              <div>
                <label className="block text-slate-300 mb-1">Company Name</label>
                <input
                  type="text"
                  value={newClientName}
                  onChange={(e) => setNewClientName(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-700 text-white rounded-lg p-2 text-xs outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. Acme Philippines Corp"
                  required
                />
              </div>
              <div>
                <label className="block text-slate-300 mb-1">BIR TIN Number</label>
                <input
                  type="text"
                  value={newClientTin}
                  onChange={(e) => setNewClientTin(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-700 text-white rounded-lg p-2 text-xs outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. 123-456-789-0000"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-300 mb-1">RDO Code</label>
                  <input
                    type="text"
                    value={newClientRdo}
                    onChange={(e) => setNewClientRdo(e.target.value)}
                    className="w-full bg-slate-900 border border-slate-700 text-white rounded-lg p-2 text-xs outline-none"
                  />
                </div>
                <div>
                  <label className="block text-slate-300 mb-1">Monthly Retainer (₱)</label>
                  <input
                    type="number"
                    value={newClientRetainer}
                    onChange={(e) => setNewClientRetainer(e.target.value)}
                    className="w-full bg-slate-900 border border-slate-700 text-white rounded-lg p-2 text-xs outline-none"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowAddClientModal(false)}
                  className="px-4 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white font-medium rounded-lg transition-colors"
                >
                  Add Client
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Log Timesheet Modal */}
      {showTimesheetModal && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-slate-800 border border-slate-700 p-6 rounded-2xl w-full max-w-md space-y-4 shadow-2xl">
            <h3 className="text-lg font-bold text-white">Log Billable Hours</h3>
            <form onSubmit={handleAddTimesheet} className="space-y-3 text-xs">
              <div>
                <label className="block text-slate-300 mb-1">Client Company</label>
                <select
                  value={newTsClient}
                  onChange={(e) => setNewTsClient(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-700 text-white rounded-lg p-2 text-xs outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {clients.map(c => (
                    <option key={c.id} value={c.companyName}>{c.companyName}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-300 mb-1">Hours Spent</label>
                  <input
                    type="number"
                    step="0.5"
                    value={newTsHours}
                    onChange={(e) => setNewTsHours(e.target.value)}
                    className="w-full bg-slate-900 border border-slate-700 text-white rounded-lg p-2 text-xs outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-slate-300 mb-1">Hourly Rate (₱)</label>
                  <input
                    type="number"
                    value={newTsRate}
                    onChange={(e) => setNewTsRate(e.target.value)}
                    className="w-full bg-slate-900 border border-slate-700 text-white rounded-lg p-2 text-xs outline-none"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-slate-300 mb-1">Task Description</label>
                <textarea
                  value={newTsTask}
                  onChange={(e) => setNewTsTask(e.target.value)}
                  rows={3}
                  className="w-full bg-slate-900 border border-slate-700 text-white rounded-lg p-2 text-xs outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. Review BIR 2307 certificates & preparation of 1604-E..."
                  required
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowTimesheetModal(false)}
                  className="px-4 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white font-medium rounded-lg transition-colors"
                >
                  Save Timesheet
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
